import { CircleCheck, CircleX, Hourglass, Info, TriangleAlert } from 'lucide-react';
import { useCallback, useId, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { Seletor } from '@/componentes/Seletor';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';


import { useLancarAbastecimento } from './consultas';
import { EtiquetaDaUltimaLeitura } from './EtiquetaDaUltimaLeitura';
import { produtoDaMaquina } from './produto-da-maquina';
import { ultimaLeituraComAFila } from './ultima-leitura';
import { AJUDA_DO_TIPO_UNICO, tanquesPermitidos, tiposPermitidos } from './trava-do-lancamento';
import {
  RASCUNHO_VAZIO,

  origemPorProduto,
  podeLancar,
  rotuloDoBotao,
  unidadeDoMedidor,
  verificacoesDoRascunho,
  type ContextoDoLancamento,
  type RascunhoDoLancamento,
} from './lancamento-guiado';
import type { EquipamentoDoModulo, SaldoDeTanque } from './tipos';
import type { NivelDaVerificacao } from './verificacao';

/**
 * O LANÇAMENTO RÁPIDO DA MESA — a mesma lógica das cinco perguntas, numa linha.
 *
 * É a tela 03 da entrega do dono (25/10/2026). O tablet e o celular perguntam
 * uma coisa por tela porque quem responde está de pé, com luva; na mesa quem
 * digita tem teclado e as notas na mão, e a mesma sequência cabe numa linha —
 * **Tab passa de campo, Enter lança**.
 *
 * As quatro perguntas são as mesmas, na mesma ordem, e as verificações são
 * as mesmas (`lancamento-guiado.ts`): o que muda é só a forma. Um segundo
 * conjunto de regras aqui faria a mesa aceitar o que o celular recusa.
 *
 * ## O TIPO ENTROU NA LINHA (26/10/2026)
 *
 * O dono: *"em lançamento eu não consigo escolher tipo"*. A linha nascia
 * `interno` e não oferecia a escolha — quem abastecia no posto tinha de trocar
 * para o formulário inteiro, e ele já havia recusado esse caminho: *"eu não
 * quero simplesmente clicar em mais campos e ele ir para completa"*.
 *
 * Abastecer no posto NÃO É EXCEÇÃO. É o outro lado do mesmo lançamento, e o
 * banco trata os dois no mesmo `insert` desde a 0008. Agora o Tipo é o
 * primeiro campo da linha, e escolher *Externo* troca a origem no lugar: sai
 * o tanque, entram o posto, o preço e o cupom, no mesmo cartão e sem recomeçar
 * o que já foi digitado.
 *
 * ## O que ele NÃO faz, e por onde se faz
 *
 * Lubrificante (ponto de aplicação, depósito), data retroativa, ordem de
 * abastecimento e as pessoas continuam no **lançamento completo**. A linha
 * responde o que se responde todo dia; o formulário responde o resto.
 */
export function LancamentoRapido({
  equipamentos,
  tanques: todosOsTanques,
  postos,
  produtos,
  medidorAnteriorDe,
  aoAbrirCompleto,
}: {
  readonly equipamentos: readonly EquipamentoDoModulo[];
  readonly tanques: readonly SaldoDeTanque[];
  /** Os postos cadastrados. Só o abastecimento EXTERNO os usa. */
  readonly postos: readonly { readonly id: string; readonly nome: string; readonly municipio: string }[];
  /** Todo produto abastecível: no externo não há tanque de onde derivar a lista. */
  readonly produtos: readonly { readonly id: string; readonly nome: string; readonly categoria: string }[];
  readonly medidorAnteriorDe: (equipamentoId: string, produtoId: string) => number | null;
  /**
   * Abre o formulário inteiro no MESMO cartão, levando a máquina já escolhida.
   *
   * A linha rápida responde quatro perguntas; o que não cabe nelas — posto com
   * cupom e preço, data retroativa, ordem, operador, obra, centro de custo —
   * está a um toque daqui, e não noutra tela.
   */
  readonly aoAbrirCompleto: (codigoDoEquipamento: string) => void;
}) {
  const lancar = useLancarAbastecimento();
  const [rascunho, setRascunho] = useState<RascunhoDoLancamento>(RASCUNHO_VAZIO);
  const base = useId();
  /* 0128 · A TRAVA DO PAPEL: os tipos e os tanques que ele libera. Com um tipo
     só, o Tipo vira o campo de opção única (fixo, com o porquê); com os
     tanques travados, o produto sai só dos liberados — e um tanque só deixa
     um produto só, que trava sozinho pela mesma regra. */
  const sessao = useSessaoEmMemoria();
  const tiposDoPapel = tiposPermitidos(sessao);
  const tanques = useMemo(() => tanquesPermitidos(todosOsTanques, sessao), [todosOsTanques, sessao]);

  const ehExterno = rascunho.tipo === 'externo';

  const contexto = useMemo<ContextoDoLancamento>(() => {
    const equipamento = equipamentos.find((e) => e.id === rascunho.equipamentoId) ?? null;
    const tanque = ehExterno ? null : (tanques.find((t) => t.produtoId === rascunho.produtoId) ?? null);
    const doPosto = produtos.find((p) => p.id === rascunho.produtoId) ?? null;
    return {
      equipamento,
      produto: tanque
        ? { id: tanque.produtoId, nome: tanque.produto, categoria: /arla/i.test(tanque.produto) ? 'arla' : 'combustivel' }
        : doPosto
          ? { id: doPosto.id, nome: doPosto.nome, categoria: doPosto.categoria === 'arla' ? 'arla' : 'combustivel' }
          : null,
      /* NULO no externo, e a verificação do saldo se desliga sozinha com a
         frase certa: "o abastecimento no posto não tira volume de tanque
         nenhum". O posto não tem saldo que o sistema conheça. */
      saldoDaOrigem: tanque?.saldoCalculadoL ?? null,
      nomeDaOrigem: tanque?.nome ?? 'o posto',
      medidorDoUltimoAbastecimento:
        equipamento && rascunho.produtoId ? medidorAnteriorDe(equipamento.id, rascunho.produtoId) : null,
      medidorSugerido: equipamento?.ultimaLeitura ?? null,
      medidorLidoEm: equipamento?.ultimaLeituraEm ?? null,
    };
  }, [equipamentos, tanques, produtos, ehExterno, rascunho.equipamentoId, rascunho.produtoId, medidorAnteriorDe]);

  /* Um tanque por produto: dois tanques do mesmo diesel davam duas opcoes
     identicas no seletor. */
  const origens = useMemo(() => origemPorProduto(tanques), [tanques]);
  /* A LISTA DE PRODUTO MUDA COM O TIPO, e é isso que faz a troca ser honesta:
     no interno só se pode tirar o que algum tanque tem; no posto se compra o
     que o posto vende, e a lista é a do cadastro de produtos. */
  const opcoesDeProduto = useMemo(
    () =>
      ehExterno
        ? produtos
            .filter((p) => p.categoria === 'combustivel' || p.categoria === 'arla')
            .map((p) => ({ valor: p.id, rotulo: p.nome }))
        : origens.map((t) => ({ valor: t.produtoId, rotulo: t.produto })),
    [ehExterno, produtos, origens],
  );
  /* Os produtos que a origem tem, na forma que `produtoDaMaquina` entende. */
  const escolhiveis = useMemo(
    () =>
      ehExterno
        ? produtos
            .filter((p) => p.categoria === 'combustivel' || p.categoria === 'arla')
            .map((p) => ({ id: p.id, nome: p.nome, categoria: p.categoria === 'arla' ? ('arla' as const) : ('combustivel' as const) }))
        : origens.map((t) => ({ id: t.produtoId, nome: t.produto, categoria: /arla/i.test(t.produto) ? ('arla' as const) : ('combustivel' as const) })),
    [ehExterno, produtos, origens],
  );
  /* TROCAR O TIPO TROCA A ORIGEM, e o produto vai junto: a lista muda, e o
     produto escolhido pode não existir na nova. O que já foi digitado — litros
     e medidor — FICA: eles são da máquina, e não da origem, e apagá-los faria
     a troca custar o dobro. É a mesma troca no seletor e no campo de opção
     única (que a faz sozinho, quando o papel libera um tipo só). */
  const trocarTipo = useCallback((valor: string) => {
    setRascunho((r) => ({
      ...r,
      tipo: valor === 'externo' ? 'externo' : 'interno',
      produtoId: null,
      postoId: null,
      preco: '',
      comprovante: '',
    }));
  }, []);
  const verificacoes = verificacoesDoRascunho(rascunho, contexto);
  const pronto = podeLancar(rascunho, contexto) && !lancar.isPending;
  const medida = unidadeDoMedidor(contexto.equipamento);

  function enviar() {
    const tanque = tanques.find((t) => t.produtoId === rascunho.produtoId);
    const litros = Number(rascunho.litros.replace(',', '.'));
    const medidor = Number(rascunho.medidor.replace(',', '.'));
    const preco = rascunho.preco.trim() === '' ? null : Number(rascunho.preco.replace(',', '.'));
    if (!pronto || rascunho.equipamentoId === null || rascunho.produtoId === null) return;
    /* A ORIGEM é o que separa os dois, e o banco cobra a coerência: interno
       exige tanque e recusa posto; externo exige posto e recusa tanque. */
    if (ehExterno ? rascunho.postoId === null : !tanque) return;
    const maquina = equipamentos.find((e) => e.id === rascunho.equipamentoId);
    lancar.mutate(
      {
        envio: {
          tipo: rascunho.tipo,
          equipamentoId: rascunho.equipamentoId,
          produtoId: rascunho.produtoId,
          ocorridoEm: new Date().toISOString(),
          quantidadeL: litros,
          ...(ehExterno
            ? {
                postoId: rascunho.postoId as string,
                comprovante: rascunho.comprovante.trim(),
                ...(preco !== null && Number.isFinite(preco) ? { precoLitro: preco } : {}),
              }
            : { tanqueId: (tanque as SaldoDeTanque).id }),
          medidor,
          /* Automáticos, como a entrega manda: a obra e o centro de custo saem
             da alocação vigente da máquina, e ninguém os digita. */
          ...(maquina?.obraId ? { obraId: maquina.obraId } : {}),
          ...(maquina?.centroCustoId ? { centroCustoId: maquina.centroCustoId } : {}),
        },
      },
      {
        onSuccess: () => {
          /* O rascunho zera e o foco volta para a máquina: quem lança em série
             — a folha do comboio inteira — não tira a mão do teclado. */
          setRascunho(RASCUNHO_VAZIO);
          document.getElementById(`${base}-maquina`)?.focus();
        },
      },
    );
  }

  return (
    <section
      aria-label="Lançamento rápido"
      className="cartao flex min-w-0 flex-col gap-3 p-4"
      onKeyDown={(evento) => {
        /* ENTER LANÇA, em qualquer campo da linha. É o que a entrega escreve
           ao lado do título, e é o que faz esta linha valer a pena: sem ele,
           quem digita teria de alcançar o botão a cada lançamento. */
        if (evento.key === 'Enter' && pronto) {
          evento.preventDefault();
          enviar();
        }
      }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="txt-secao text-tinta">Lançamento rápido</h2>
          <span className="txt-apoio text-tinta-secundaria">Tab passa de campo · Enter lança</span>
        </div>
        {/* O resto dos campos, no mesmo cartão: posto com cupom e preço, data
            retroativa, ordem e as pessoas. Secundário, porque o laranja desta
            tela é o "Lançar" da própria linha. */}
        <Botao
          nivel="secundario"
          onClick={() => {
            aoAbrirCompleto(equipamentos.find((e) => e.id === rascunho.equipamentoId)?.codigo ?? '');
          }}
        >
          Mais campos
        </Botao>
      </div>

      {/* O TIPO É O PRIMEIRO CAMPO, e fica numa linha própria acima dos
          outros: ele não é mais um dado do lançamento, é o que decide QUAIS
          são os outros campos. Pô-lo no meio da linha faria a linha mudar de
          forma à direita de um campo que já tinha sido respondido. */}
      <div className="flex flex-wrap items-center gap-2">
        {tiposDoPapel.length === 1 ? (
          <div className="min-w-60">
            <CampoSelecao
              rotulo="Tipo"
              valor={rascunho.tipo}
              opcoes={tiposDoPapel.map((t) => ({ valor: t, rotulo: t === 'interno' ? 'Do tanque ou comboio' : 'No posto' }))}
              porQueUnica={AJUDA_DO_TIPO_UNICO}
              aoMudar={trocarTipo}
            />
          </div>
        ) : (
          <>
        <span className="txt-rotulo-campo" id={`${base}-tipo-rotulo`}>
          Tipo
        </span>
        <Seletor
          rotulo="Tipo do abastecimento"
          valor={rascunho.tipo}
          opcoes={[
            { valor: 'interno', rotulo: 'Do tanque ou comboio' },
            { valor: 'externo', rotulo: 'No posto' },
          ]}
          aoEscolher={trocarTipo}
        />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(12rem,1.4fr)_minmax(10rem,1fr)_8rem_10rem_auto]">
        <CampoSelecao
          id={`${base}-maquina`}
          rotulo="Máquina"
          valor={rascunho.equipamentoId ?? ''}
          rotuloVazio="Escolha a máquina"
          opcoes={equipamentos.map((e) => opcaoDaMaquina(e))}
          adesivo
          aoMudar={(valor) => {
            /* Trocar de máquina zera o medidor: ele é a leitura DAQUELA
               máquina, e carregá-lo para outra é o erro mais caro que esta
               linha pode cometer em silêncio.
               E JÁ ESCOLHE O PRODUTO: a máquina tem um — o último que ela
               recebeu, ou o que a ficha dela diz que o motor queima. Deixar a
               pergunta aberta num lugar onde a resposta quase nunca muda é
               pedir um toque por lançamento, a troco de nada. */
            const maquina = equipamentos.find((e) => e.id === valor) ?? null;
            setRascunho((r) => ({
              ...r,
              equipamentoId: valor === '' ? null : valor,
              produtoId: produtoDaMaquina(maquina, escolhiveis, origens) ?? r.produtoId,
              medidor: '',
            }));
          }}
        />

        <div className="flex min-w-0 flex-col gap-1">
          <span className="txt-rotulo-campo">Produto</span>
          {opcoesDeProduto.length === 0 ? (
            <span className="txt-apoio text-tinta-secundaria">
              {ehExterno ? 'Nenhum combustível cadastrado' : 'Nenhum tanque com saldo'}
            </span>
          ) : (
            <Seletor
              rotulo="Produto"
              valor={rascunho.produtoId ?? ''}
              opcoes={opcoesDeProduto}
              aoEscolher={(valor) => {
                setRascunho((r) => ({ ...r, produtoId: valor }));
              }}
            />
          )}
        </div>

        <Campo id={`${base}-litros`} rotulo="Litros">
          <div className="flex items-center gap-2">
            <input
              id={`${base}-litros`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={rascunho.litros}
              placeholder="180"
              className="controle w-full"
              onChange={(evento) => {
                setRascunho((r) => ({ ...r, litros: evento.target.value }));
              }}
            />
            <span className="txt-corpo shrink-0 text-tinta-apagada">L</span>
          </div>
        </Campo>

        {/* SEM ajuda debaixo do campo: debaixo dele a frase empurrava o rótulo
            para cima e desalinhava a linha inteira. A última leitura fica na
            ETIQUETA AO LADO DO RÓTULO (decisão 54, 28/10/2026), que é onde quem
            digita o número olha. */}
        <Campo
          id={`${base}-medidor`}
          rotulo="Medidor"
          {...(contexto.equipamento
            ? { etiqueta: <EtiquetaDaUltimaLeitura curta leitura={ultimaLeituraComAFila(contexto.equipamento)} unidade={medida} /> }
            : {})}
        >
          <div className="flex items-center gap-2">
            <input
              id={`${base}-medidor`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={rascunho.medidor}
              placeholder="4.832"
              className="controle w-full"
              onChange={(evento) => {
                setRascunho((r) => ({ ...r, medidor: evento.target.value }));
              }}
            />
            <span className="txt-corpo shrink-0 text-tinta-apagada">{medida}</span>
          </div>
        </Campo>

        <Botao nivel="primario" disabled={!pronto} onClick={enviar}>
          {rotuloDoBotao('confirmacao', rascunho)}
        </Botao>
      </div>

      {/* A SEGUNDA LINHA, SÓ NO POSTO: ela aparece no mesmo cartão, abaixo da
          primeira, e não noutra tela. São os três campos que o abastecimento
          externo tem a mais — e o cupom é obrigatório porque o banco o exige
          (0008): externo sem comprovante é pagamento sem lastro. */}
      {ehExterno ? (
        <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(14rem,1.6fr)_8rem_minmax(10rem,1fr)]">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="txt-rotulo-campo">Posto</span>
            {postos.length === 0 ? (
              <span className="txt-apoio text-tinta-secundaria">Nenhum posto cadastrado</span>
            ) : (
              <Seletor
                rotulo="Posto"
                valor={rascunho.postoId ?? ''}
                opcoes={postos.map((p) => ({
                  valor: p.id,
                  rotulo: p.municipio === '' ? p.nome : `${p.nome} · ${p.municipio}`,
                }))}
                aoEscolher={(valor) => {
                  setRascunho((r) => ({ ...r, postoId: valor }));
                }}
              />
            )}
          </div>

          <Campo id={`${base}-preco`} rotulo="Preço" opcional>
            <div className="flex items-center gap-2">
              <span className="txt-corpo shrink-0 text-tinta-apagada">R$</span>
              <input
                id={`${base}-preco`}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={rascunho.preco}
                placeholder="6,41"
                className="controle w-full"
                onChange={(evento) => {
                  setRascunho((r) => ({ ...r, preco: evento.target.value }));
                }}
              />
            </div>
          </Campo>

          <Campo id={`${base}-cupom`} rotulo="Cupom">
            <input
              id={`${base}-cupom`}
              type="text"
              autoComplete="off"
              value={rascunho.comprovante}
              placeholder="Número do cupom ou da nota"
              className="controle w-full"
              onChange={(evento) => {
                setRascunho((r) => ({ ...r, comprovante: evento.target.value }));
              }}
            />
          </Campo>
        </div>
      ) : null}

      {/* AS QUATRO VERIFICAÇÕES, em etiquetas — a mesma lista do painel do
          celular, no espaço de uma linha. Elas aparecem enquanto se digita,
          e não só no envio: é isso que faz a pessoa corrigir o número com a
          nota ainda na mão. */}
      <ul className="flex flex-wrap gap-2">
        {verificacoes.map((v) => (
          <li key={v.titulo}>
            <EtiquetaDaVerificacao nivel={v.nivel} titulo={v.titulo} detalhe={v.detalhe} />
          </li>
        ))}
      </ul>
    </section>
  );
}

const ICONE: Readonly<Record<NivelDaVerificacao, typeof CircleCheck>> = {
  ok: CircleCheck,
  atencao: TriangleAlert,
  erro: CircleX,
  pendente: Hourglass,
  naoSeAplica: Info,
};

const TOM: Readonly<Record<NivelDaVerificacao, string>> = {
  ok: 'bg-sucesso-fundo text-sucesso-texto',
  atencao: 'bg-atencao-fundo text-atencao-texto',
  erro: 'bg-erro-fundo text-erro-texto',
  pendente: 'bg-rebaixada text-tinta-secundaria',
  naoSeAplica: 'bg-rebaixada text-tinta-secundaria',
};

function EtiquetaDaVerificacao({
  nivel,
  titulo,
  detalhe,
}: {
  readonly nivel: NivelDaVerificacao;
  readonly titulo: string;
  readonly detalhe: string;
}) {
  const Icone = ICONE[nivel];
  return (
    <span
      title={detalhe}
      {...(nivel === 'erro' ? { role: 'alert' as const } : {})}
      /* `relative` NÃO É DECORAÇÃO: sem ele, o `sr-only` de baixo — que é
         `position: absolute` — se ancora na casca da aplicação, escapa da área
         de rolagem e estica o DOCUMENTO. Foi assim que esta tela passou a ter
         dois cortes de rolagem e a perder o cabeçalho e o menu ao rolar
         (25/10/2026). O verificador de `lib/rolagemLateral.ts` acusa isso em
         desenvolvimento, nos dois eixos. */
      className={`relative inline-flex h-[var(--selo-altura)] items-center gap-1.5 rounded-pill px-2 ${TOM[nivel]}`}
    >
      <Icone aria-hidden="true" className="size-[var(--icone-controle)] shrink-0" />
      <span className="txt-selo">{titulo}</span>
      {/* O detalhe inteiro para quem lê por leitor de tela: `title` sozinho
          não é anunciado, e a etiqueta ficaria sendo só três palavras. */}
      <span className="sr-only">{`. ${detalhe}`}</span>
    </span>
  );
}


